import React, { useEffect, useRef, type FC, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';

import { useAssistant } from '@core/assistant/providers/Provider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';

import { EditorAiDataProvider } from './AiScripts/EditorAiDataContext';
import {
  REQUIRE_SPOT_TRIGGER_LINK,
  useRegionIndex,
} from './AiScripts/useRegionIndex';
import { DriftProvider } from './DriftContext';
import { EditorModeProvider, useEditorMode } from './EditorModeContext';
import { MapEditorProvider, useMapEditor } from './MapEditorContext';
import { useActiveBrandingId } from './useActiveBrandingId';
import { useCanUseMapEditor } from './useCanUseMapEditor';
import { useMapEditorCatalog } from './useMapEditorCatalog';
import { EditorNodeKind } from './editorCategories';
import {
  clearPreviewLayoutInUnreal,
  previewLayoutInUnreal,
  setEditorModeInUnreal,
  shouldUnrealEditorModeBeOn,
  spawnLayoutInUnreal,
} from './mapEditorUnreal';
import SceneTree from './SceneTree/SceneTree';
import Inspector from './Inspector/Inspector';
import BrandingBar from './BrandingBar/BrandingBar';
import { useEditorStyles } from './styles';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

export const UESelectionBridge = () => {
  const {
    selectedCustomizationUeId,
    customizationSelectionRevision,
    currentRoom,
    suppressedCustomizationSelectIdRef,
  } = useMytaverseEvent();
  const assistant = useAssistant();
  const mapName =
    currentRoom?.map?.name ?? assistant?.currentMapName ?? undefined;
  const regionIndex = useRegionIndex(mapName);
  const {
    selectSpot,
    selectRegionScript,
    setActiveFocus,
    activeFocus,
    setAiTreeSection,
    registerSceneClick,
  } = useEditorMode();

  useEffect(() => {
    if (!selectedCustomizationUeId) {
      return;
    }
    // Unreal echoes back a CUSTOMISATION_ITEM_SELECTED for the spot a
    // teleport-to-spot just landed on — Inspector.tsx's teleport handler
    // marks that ueId here right beforehand so this specific re-selection
    // (which would otherwise reopen a pane the handler just closed) is
    // skipped exactly once, instead of every future legitimate selection.
    if (
      suppressedCustomizationSelectIdRef.current === selectedCustomizationUeId
    ) {
      suppressedCustomizationSelectIdRef.current = null;
      return;
    }
    // Always remember the click — the AI Scripts create popover surfaces
    // these as "Recent scene clicks" even after the user switches focus.
    registerSceneClick(selectedCustomizationUeId);
    if (activeFocus === 'assistant') {
      const isLinkedToTrigger =
        !!regionIndex.spotToRegion[selectedCustomizationUeId.toLowerCase()];
      if (!REQUIRE_SPOT_TRIGGER_LINK || isLinkedToTrigger) {
        // In AI focus, a spot click opens the AI inspector for that spot's
        // trigger. Spots live in the unified Scripts section, so make sure
        // it's active if the user was browsing Knowledge.
        setAiTreeSection('scripts');
        selectRegionScript(selectedCustomizationUeId, null);
        return;
      }
      // Not linked to a trigger — no AI script/knowledge config for it, fall
      // back to the plain Spot inspector instead.
      setActiveFocus('spots');
      selectSpot(selectedCustomizationUeId);
      return;
    }
    if (activeFocus !== 'spots') {
      setActiveFocus('spots');
    }
    selectSpot(selectedCustomizationUeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomizationUeId, customizationSelectionRevision, regionIndex]);

  return null;
};

const HotspotPlacementBridge = () => {
  const {
    pendingEditorHotspotSelection,
    setPendingEditorHotspotSelection,
    pendingHotspotPlacement,
    setPendingHotspotPlacement,
  } = useMytaverseEvent();
  const { selectHotspot, setActiveFocus, activeFocus, selectedNode } =
    useEditorMode();
  const wasEditingPlacedHotspotRef = useRef(false);

  useEffect(() => {
    if (!pendingEditorHotspotSelection) {
      return;
    }
    if (activeFocus !== 'hotspots') {
      setActiveFocus('hotspots');
    }
    selectHotspot(pendingEditorHotspotSelection.hotspotId);
    setPendingEditorHotspotSelection(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEditorHotspotSelection]);

  useEffect(() => {
    const placedHotspotId = pendingHotspotPlacement?.isCreated
      ? pendingHotspotPlacement.brandingHotspotId
      : null;
    if (!placedHotspotId) {
      wasEditingPlacedHotspotRef.current = false;
      return;
    }
    const isEditingPlacedHotspot =
      selectedNode?.kind === EditorNodeKind.HOTSPOT &&
      selectedNode.hotspotId === placedHotspotId;
    if (isEditingPlacedHotspot) {
      wasEditingPlacedHotspotRef.current = true;
      return;
    }
    if (wasEditingPlacedHotspotRef.current) {
      wasEditingPlacedHotspotRef.current = false;
      setPendingHotspotPlacement(null);
    }
  }, [selectedNode, pendingHotspotPlacement, setPendingHotspotPlacement]);

  useEffect(
    () => () => {
      setPendingHotspotPlacement(null);
    },
    [setPendingHotspotPlacement],
  );

  return null;
};

// When "follow live trigger" is enabled, walking into an AI trigger
// (SYNC_DATA_UE.audioTrigger.aiTriggerId) auto-opens the inspector for that
// trigger and switches the left panel to AI-trigger mode. When disabled, the
// virtual assistant (Noa) plays normally and the tree only highlights the row.
export const AiTriggerSelectionBridge = () => {
  const { aiTriggerName, currentRoom } = useMytaverseEvent();
  const assistant = useAssistant();
  const mapName =
    currentRoom?.map?.name ?? assistant?.currentMapName ?? undefined;
  const regionIndex = useRegionIndex(mapName);
  const {
    activeFocus,
    followLiveTrigger,
    selectRegionScript,
    setAiTreeSection,
    selectedNode,
    suppressedTriggerSelectRef,
  } = useEditorMode();

  useEffect(() => {
    if (!followLiveTrigger || activeFocus !== 'assistant' || !aiTriggerName) {
      return;
    }
    if (suppressedTriggerSelectRef.current === aiTriggerName) {
      suppressedTriggerSelectRef.current = null;
      return;
    }
    // Defensive: only auto-open for a real region or a spot linked to one —
    // a live trigger name that resolves to neither has no AI script/knowledge
    // surface to show.
    const isLinkedToTrigger =
      regionIndex.regionNames.includes(aiTriggerName) ||
      !!regionIndex.spotToRegion[aiTriggerName.toLowerCase()];
    if (REQUIRE_SPOT_TRIGGER_LINK && !isLinkedToTrigger) {
      return;
    }
    // Skip if the inspector is already showing this trigger — avoids resetting
    // the panel as repeated SYNC_DATA_UE bursts arrive for the same trigger.
    if (
      selectedNode?.kind === EditorNodeKind.REGION_SCRIPT &&
      selectedNode.trigger === aiTriggerName
    ) {
      return;
    }
    setAiTreeSection('scripts');
    selectRegionScript(aiTriggerName, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTriggerName, followLiveTrigger, activeFocus, regionIndex]);

  return null;
};

export const MapEditorModeBridge = () => {
  const { activeFocus } = useEditorMode();
  const { sendMessageToUnreal } = useMytaverse();
  const canUseMapEditor = useCanUseMapEditor();
  const shouldBeOn = shouldUnrealEditorModeBeOn(activeFocus, canUseMapEditor);

  const sendRef = useRef(sendMessageToUnreal);
  sendRef.current = sendMessageToUnreal;

  const sentEnabledRef = useRef(false);

  useEffect(() => {
    if (shouldBeOn === sentEnabledRef.current) {
      return;
    }
    sentEnabledRef.current = shouldBeOn;
    setEditorModeInUnreal(sendRef.current, shouldBeOn);
  }, [shouldBeOn]);

  useEffect(
    () => () => {
      if (!sentEnabledRef.current) {
        return;
      }
      sentEnabledRef.current = false;
      setEditorModeInUnreal(sendRef.current, false);
    },
    [],
  );

  return null;
};

export const MapEditorPreviewBridge = () => {
  const { activeFocus } = useEditorMode();
  const { chain, hasAuthorEdits } = useMapEditor();
  const { sendMessageToUnreal } = useMytaverse();
  const canUseMapEditor = useCanUseMapEditor();
  const isActive = activeFocus === 'mapEditor' && canUseMapEditor;

  const sendRef = useRef(sendMessageToUnreal);
  sendRef.current = sendMessageToUnreal;

  const { elements: catalogElements } = useMapEditorCatalog();
  const catalogRef = useRef(catalogElements);
  catalogRef.current = catalogElements;

  const chainKey = chain.join('\0');
  const hasPreviewedRef = useRef(false);

  useEffect(() => {
    if (!isActive || !hasAuthorEdits) {
      return;
    }
    hasPreviewedRef.current = true;
    previewLayoutInUnreal(
      sendRef.current,
      chainKey ? chainKey.split('\0') : [],
      catalogRef.current,
    );
  }, [isActive, chainKey, hasAuthorEdits]);

  // A draft that stops being the author's own work has to be taken down, not just stop updating.
  // revertDraft is the case: it puts the standing layout back in the list, and previewing that would
  // build a local copy of rooms the server is already showing — with the published ones hidden
  // behind it, since Unreal hides them for as long as any draft is up. Tearing down instead hands
  // the real rooms back, which is what reverting means.
  //
  // Kept here rather than in whoever calls revertDraft: previews are this bridge's to own, and a
  // second sender would be a second place to keep the draft-visibility flag in step.
  useEffect(() => {
    if (hasAuthorEdits || !hasPreviewedRef.current) {
      return;
    }
    hasPreviewedRef.current = false;
    clearPreviewLayoutInUnreal(sendRef.current);
  }, [hasAuthorEdits]);

  useEffect(
    () => () => {
      if (hasPreviewedRef.current) {
        hasPreviewedRef.current = false;
        clearPreviewLayoutInUnreal(sendRef.current);
      }
    },
    [],
  );

  return null;
};

const MapEditorGeometryBridge = () => {
  const { currentGeometryRoomIds, currentGeometryRefusals } =
    useMytaverseEvent();
  const { setBuiltRoomIds, setRefusals } = useMapEditor();

  useEffect(() => {
    setBuiltRoomIds(currentGeometryRoomIds);
  }, [currentGeometryRoomIds, setBuiltRoomIds]);

  // Separate effect from the room ids on purpose: setBuiltRoomIds also seeds the draft on its first
  // call, and folding the refusals into that would tie a report of what was turned down to a
  // one-shot piece of state it has nothing to do with.
  useEffect(() => {
    setRefusals(currentGeometryRefusals);
  }, [currentGeometryRefusals, setRefusals]);

  return null;
};

export const MapEditorPersistenceBridge = () => {
  const currentBrandingId = useActiveBrandingId();
  const { hydrateFromPersisted } = useMapEditor();
  const { sendMessageToUnreal } = useMytaverse();
  const { elements: catalogElements } = useMapEditorCatalog();

  const canSpawnLayout = useCanUseMapEditor();

  const sendRef = useRef(sendMessageToUnreal);
  sendRef.current = sendMessageToUnreal;
  const catalogRef = useRef(catalogElements);
  catalogRef.current = catalogElements;
  const canSpawnRef = useRef(canSpawnLayout);
  canSpawnRef.current = canSpawnLayout;

  useEffect(() => {
    if (!currentBrandingId) {
      return undefined;
    }

    // Guards against a branding switch resolving out of order: a slow response for the
    // branding the author just left must not overwrite the one they are now on.
    let cancelled = false;

    BrandingsService.getBranding(currentBrandingId)
      .then((branding) => {
        const rooms = branding?.layout?.rooms;
        if (cancelled || !Array.isArray(rooms)) {
          return;
        }
        const roomIds = rooms.filter((id) => typeof id === 'string' && !!id);
        hydrateFromPersisted(roomIds);

        if (!canSpawnRef.current || !roomIds.length) {
          return;
        }
        spawnLayoutInUnreal(sendRef.current, roomIds, catalogRef.current);
      })
      .catch((error: Error) => {
        console.error(
          `Failed to load the saved map layout: ${error?.message}`,
          error,
        );
      });

    return () => {
      cancelled = true;
    };
  }, [currentBrandingId, hydrateFromPersisted]);

  return null;
};

const EditorModeContent = () => {
  const styles = useEditorStyles();
  const { setBrandingActionRequest } = useMytaverseEvent();

  return (
    <Box sx={styles.root}>
      <UESelectionBridge />
      <HotspotPlacementBridge />
      <AiTriggerSelectionBridge />
      <MapEditorModeBridge />
      <MapEditorPreviewBridge />
      <MapEditorGeometryBridge />
      <SceneTree />
      <Inspector />
      <Box sx={styles.brandingBarWrap}>
        <BrandingBar
          onCreateNew={() => setBrandingActionRequest('create')}
          onOpenLibrary={() => setBrandingActionRequest('library')}
        />
      </Box>
    </Box>
  );
};

const EditorMode = () => {
  const { isBrandingModeEnabled, brandingEditorFocus } = useMytaverseEvent();

  return (
    <MapEditorProvider>
      <MapEditorPersistenceBridge />
      <AnimatePresence>
        {isBrandingModeEnabled ? (
          <motion.div
            key="editor-mode"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 1200,
            }}
          >
            <EditorModeProvider initialFocus={brandingEditorFocus}>
              <EditorAiDataProvider>
                <DriftProvider>
                  <EditorModeContent />
                </DriftProvider>
              </EditorAiDataProvider>
            </EditorModeProvider>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </MapEditorProvider>
  );
};

export default EditorMode;
